import { format, formatDistance } from "date-fns";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Confirm, Icon, Popup, Table } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import {
  convertFileTypeToIcon,
  convertFullName,
  docIsTypeImage,
  docIsTypeLink,
  docIsTypePdf,
  downloadNonPdf,
  downloadPdf,
  documentHasAgentSigningOpportunity,
  partyIsAgent,
  partyIsBuyerAgent,
  partyIsClient,
  partyIsListingAgent,
  hasOtherSideClients,
  isPartyCoAgentUser,
  isPartyTcUser,
  isPartyAssistantUser,
} from "../../../app/common/util/util";
import {
  addHistoryToDb,
  updateDocInDb,
} from "../../../app/firestore/firestoreService";
import { getFormFieldData } from "../../docs/docComponents/docFill/formFieldData/formFieldData";
import { openModal } from "../../../app/common/modals/modalSlice";
import AgentSendForSigningButton from "./AgentSendForSigningButton";
import AgentShareButton from "./AgentShareButton";

export default function PartyDocViewListItem({ doc }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { party } = useSelector((state) => state.party);
  const { transaction, allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { currentUser } = useSelector((state) => state.auth);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [isSigning, setIsSigning] = useState(false);
  const [confirmSignOpen, setConfirmSignOpen] = useState(false);

  const docIsLink = docIsTypeLink(doc);

  function handleView() {
    addHistoryToDb(doc.transactionId, party, "viewed", doc.name);
    if (docIsLink) {
      if (doc.linkUrl) {
        window.open(doc.linkUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("No link found for this document.");
      }
    } else if (docIsTypeImage(doc)) {
      dispatch(
        openModal({
          modalType: "DocViewImageFileModal",
          modalProps: { doc: doc },
        })
      );
    } else {
      navigate(`/party/${party.id}/documents/${doc.id}/view`);
    }
  }

  function handleFill() {
    navigate(`/party/${party.id}/documents/${doc.id}/fill`);
  }

  async function handleDownload() {
    addHistoryToDb(doc.transactionId, party, "downloaded", doc.name);
    if (docIsTypePdf(doc)) {
      downloadPdf(doc, transaction, currentUserProfile);
    } else if (doc.filetypeMIME) {
      downloadNonPdf(doc);
    } else {
      toast.error("Error with downloading document.");
    }
  }

  // Check if this agent can send this document for signing
  // Also show the button if the document was already sent by this agent (to show "Waiting for Signatures" status)
  const wasSentByThisAgent = doc.sentForSigningByAgent && doc.sentByAgentId === party.id;
  const canSendForSigning =
    !docIsLink &&
    partyIsAgent(party.role) && (documentHasAgentSigningOpportunity(doc, party) || wasSentByThisAgent);

  // Show a Share button only when the Send for Signing / Waiting for Signatures /
  // Clients Signed button is not present. If that button exists the document
  // will already appear in the clients' portal via the signing flow.
  const canShare =
    !canSendForSigning &&
    partyIsAgent(party.role) &&
    hasOtherSideClients(allParties, transaction.agentRepresents);

  // Determine if this agent can sign the broker signature field on this document.
  // The Listing Agent (Seller Agent) signs the Seller Broker field, while the
  // Buyer Agent (Buyer Broker) signs the Buyer Broker field.
  const isListingAgent = partyIsListingAgent(party.role);
  const isBuyerAgent = partyIsBuyerAgent(party.role);

  // A party's co-agent, TC, and assistant all share the same party portal
  // link, so the logged-in person must be identified by email to know who
  // they actually are - only the agent or their co-agent may sign the
  // broker/co-agent field; a TC or assistant is never an authorized signer.
  // Prefer the Firebase Auth email (available synchronously at sign-in) over
  // the Firestore profile doc, which loads async and may lag on first render.
  const loggedInEmail = currentUser?.email || currentUserProfile?.email;
  const isCoAgentUser = isPartyCoAgentUser(party, loggedInEmail);
  const isTcUser = isPartyTcUser(party, loggedInEmail);
  const isAssistantUser = isPartyAssistantUser(party, loggedInEmail);
  const isAuthorizedSigner = !isTcUser && !isAssistantUser;
  const signer = isCoAgentUser ? party.coAgent : party;

  const { signatureFieldName, dateFieldName, canSign, hasSigned, signedAt } = useMemo(() => {
    if (!docIsTypePdf(doc) || (!isListingAgent && !isBuyerAgent)) {
      return { signatureFieldName: null, dateFieldName: null, canSign: false, hasSigned: false, signedAt: null };
    }
    const formFields = getFormFieldData(doc.title, transaction);
    const findFieldName = (candidates) =>
      candidates.find((name) => formFields?.some((field) => field.name === name)) || null;

    // Co-agent field names are inconsistent across form versions
    // (e.g. "Signature Seller CoAgent" vs "Signature CoAgent Seller"),
    // so check every known variant against the form's actual fields.
    let sigField;
    let dtField;
    if (isCoAgentUser) {
      sigField = isListingAgent
        ? findFieldName(["Signature Seller CoAgent", "Signature CoAgent Seller"])
        : findFieldName(["Signature Buyer CoAgent"]);
      dtField = isListingAgent
        ? findFieldName(["Date Signature Seller CoAgent", "Date Signature CoAgent Seller"])
        : findFieldName(["Date Signature Buyer CoAgent"]);
    } else {
      sigField = findFieldName([isListingAgent ? "Signature Seller Broker" : "Signature Buyer Broker"]);
      dtField = isListingAgent ? "Date Signature Seller Broker" : "Date Signature Buyer Broker";
    }

    if (!sigField) {
      return { signatureFieldName: null, dateFieldName: null, canSign: false, hasSigned: false, signedAt: null };
    }
    // Check if already signed via formFieldValues
    if (doc.formFieldValues?.[sigField]) {
      const rawDate = doc.formFieldValues?.[dtField];
      const dateObj = rawDate?.toDate ? rawDate.toDate() : rawDate instanceof Date ? rawDate : null;
      const formattedDate = dateObj ? format(dateObj, "MMM d, yyyy h:mm a") : null;
      return { signatureFieldName: sigField, dateFieldName: dtField, canSign: false, hasSigned: true, signedAt: formattedDate };
    }
    // The signer must not have already signed via an annotation
    const allAnnots = [
      ...(doc.annots || []),
      ...(doc.annotsInProgress || []),
      ...(doc.annotsInProgressSuggested || []),
      ...Object.values(doc.annotsByAgent || {}),
    ];
    const signedViaAnnot = allAnnots.some(
      (annot) =>
        annot.agentsField &&
        annot.signatureFieldName === sigField &&
        (annot.signed === true || annot.value)
    );
    return {
      signatureFieldName: sigField,
      dateFieldName: dtField,
      // A TC or assistant can see the field is unsigned but isn't an
      // authorized signer, so they never get the Sign button for it.
      canSign: !signedViaAnnot && isAuthorizedSigner,
      hasSigned: signedViaAnnot,
      signedAt: null,
    };
  }, [doc, transaction, isListingAgent, isBuyerAgent, isCoAgentUser, isAuthorizedSigner]);

  async function handleSign() {
    setIsSigning(true);

    try {
      await updateDocInDb(doc.id, {
        [`formFieldValues.${signatureFieldName}`]: convertFullName(signer).trim(),
        [`formFieldValues.${dateFieldName}`]: new Date(),
      });
      try {
        await addHistoryToDb(
          doc.transactionId,
          isCoAgentUser ? { ...party.coAgent, role: "CoAgent" } : party,
          "Agent signed",
          doc.name,
          currentUserProfile
        );
      } catch (error) {
        console.log("silent error adding agent signed to history ", error);
      }
      toast.success("Document signed successfully!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSigning(false);
      setConfirmSignOpen(false);
    }
  }

  return (
    <Table.Row key={doc.id}>
      <Table.Cell>
        <Icon
          name={
            docIsLink ? "linkify" : `file ${convertFileTypeToIcon(doc)} outline`
          }
          size="large"
        />
        &nbsp;
        {doc.name}
      </Table.Cell>
      <Table.Cell>
        <p>
          {doc.updatedAt && doc.updatedAt instanceof Date
            ? formatDistance(doc.updatedAt, new Date(), { addSuffix: true })
            : ""}
        </p>
      </Table.Cell>
      <Table.Cell>
        {!docIsLink && (
          <Button
            floated={isMobile ? null : "right"}
            className={isMobile ? "fluid medium bottom margin" : null}
            size="small"
            onClick={() => handleDownload()}
          >
            Download
          </Button>
        )}
        {canSendForSigning && (
          <AgentSendForSigningButton
            doc={doc}
            party={party}
            transaction={transaction}
            allParties={allParties}
            isMobile={isMobile}
          />
        )}
        {canShare && (
          <AgentShareButton
            doc={doc}
            party={party}
            transaction={transaction}
            allParties={allParties}
            isMobile={isMobile}
          />
        )}
        {hasSigned && (
          <Popup
            trigger={
              <Button
                floated={isMobile ? null : "right"}
                className={isMobile ? "fluid medium bottom margin" : null}
                size="small"
                disabled
              >
                {/* <Icon name="check" /> */}
                 I Signed
              </Button>
            }
            content={signedAt ? `You signed on ${signedAt}` : "You have signed this document"}
            position="top center"
          />
        )}
        {canSign && (
          <>
            <Button
              color="blue"
              floated={isMobile ? null : "right"}
              className={isMobile ? "fluid medium bottom margin" : null}
              size="small"
              loading={isSigning}
              disabled={isSigning}
              onClick={() => setConfirmSignOpen(true)}
            >
              <Icon name="pencil" /> Sign
            </Button>
            <Confirm
              open={confirmSignOpen}
              header="Sign Document"
              content={`Sign "${doc.name}" as ${convertFullName(
                signer
              )}? Your signature and today's date will be added to the document.`}
              confirmButton="Sign"
              onCancel={() => setConfirmSignOpen(false)}
              onConfirm={handleSign}
            />
          </>
        )}
        {doc.isFillableByClient &&
          doc.subStatus !== "Awaiting Signatures" &&
          doc.status !== "Complete" &&
          partyIsClient(transaction, party) && (
            <Button
              primary
              floated={isMobile ? null : "right"}
              className={isMobile ? "fluid medium bottom margin" : null}
              size="small"
              onClick={() => handleFill()}
            >
              Fill Out
            </Button>
          )}
        {(docIsLink || docIsTypePdf(doc) || docIsTypeImage(doc)) && (
          <Button
            floated={isMobile ? null : "right"}
            className={isMobile ? "fluid medium bottom margin" : null}
            size="small"
            onClick={() => handleView()}
          >
            View
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  );
}
